/**
 * Files module transport (#93): the files.* commands through the TYPO3
 * proxy (type=3002). Proxy mode only. Same rules as every proxy call: CSRF
 * header, session cookie, session loss → pending, one CSRF refresh + retry.
 * The proxy translates to gd.Document.* and decides persistence and rights
 * (gitterdan-website#53); this class only sends and parses.
 * Folders (#123): list per folder, mkdir/rename/delete folders, move documents.
 */

import { buildProxyHeaders, type AuthService } from './auth-utils';
import {
  GatewayError,
  GatewayErrorCode,
  isGatewayError,
} from './gateway/error-codes';
import {
  proxyPost,
  throwIfCoded,
  type ProxyRequestBuilder,
} from './gateway/proxy-post';

export interface FileItem {
  kind: 'file';
  id: string;
  name: string;
  mimeType: string;
  size: number;
  language: string | null;
  createdAt: string;
  uploadedBy: { name: string } | null;
  /**
   * Own documents: processing | ready | failed. Nextcloud files (#98 part 4): their analysis
   * state; 'external' when the analysis state is not known (no actions).
   */
  status:
    | 'processing'
    | 'queued'
    | 'preparing'
    | 'ready'
    | 'failed'
    | 'external'
    | NextcloudAnalysisState;
  /** Flat collection listing only: where the document sits. */
  path?: string;
  /** nextcloud.add answer: the collection state of the added file. */
  collection?: {
    status: string;
    documentId: string | null;
    /** The file changed in Nextcloud since it was added. */
    changed?: boolean;
  };
  canDelete: boolean;
  canMove: boolean;
  /** #98 part 4, Nextcloud files only. */
  analysis?: {
    state: NextcloudAnalysisState;
    analyzedAt?: string;
    /** 'ANALYSIS_FAILED' when the last job failed. */
    error?: string;
  };
  canAnalyze?: boolean;
  canOpen?: boolean;
  /** Only when the file changed in Nextcloud since loading. */
  canLoadNewVersion?: boolean;
}
export type NextcloudAnalysisState =
  'notAnalyzed' | 'analyzing' | 'analyzed' | 'outdated' | 'changed' | 'failed';
export interface FolderItem {
  kind: 'folder';
  id: string;
  name: string;
  createdAt: string;
  createdBy: { name: string } | null;
  canRename: boolean;
  canDelete: boolean;
  /** Nextcloud folders (#98 part 3): where the folder comes from. */
  mountType?: 'own' | 'shared' | 'group' | 'external';
  /** Exposed Nextcloud folders: false when moved, renamed or deleted in Nextcloud. */
  available?: boolean;
}
export type ListItem = FolderItem | FileItem;

/** The Files root row that opens the Nextcloud folder; '#' keeps it apart from every gateway id. */
export const NEXTCLOUD_MOUNT_ID = '#nextcloud';
export interface FolderRef {
  id: string;
  name: string;
}
export interface FolderListing {
  folder: {
    id: string | null;
    name: string;
    canRename: boolean;
    canDelete: boolean;
    /** #98 part 3: undefined = true (own documents) */
    canCreateFolder?: boolean;
    /** #98 part 3: undefined = true (own documents); false in Nextcloud */
    canUpload?: boolean;
  };
  path: FolderRef[];
  items: ListItem[];
  page: number;
  pageSize: number;
  total: number;
}

/** gitterdan-website#52: the proxy accepts up to 50 MB. */
export const FILES_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export default class FilesService {
  constructor(
    private readonly options: { proxyUrl: string; authService: AuthService }
  ) {}

  async list(
    folderId: string | null = null,
    page = 1,
    pageSize = 25
  ): Promise<FolderListing> {
    const data = (await this.command('files.list', {
      folderId,
      page,
      pageSize,
    })) as Partial<FolderListing>;
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      folder: data.folder ?? {
        id: null,
        name: '',
        canRename: false,
        canDelete: false,
      },
      path: Array.isArray(data.path) ? data.path : [],
      // An item without kind is a document (the proxy before #123 sent none).
      items: items.map((item) =>
        item.kind === 'folder' ? item : { ...item, kind: 'file' as const }
      ),
      page: data.page ?? page,
      pageSize: data.pageSize ?? pageSize,
      total: data.total ?? 0,
    };
  }

  /** The whole collection, documents only (files.list with flat). */
  async listCollection(
    opts: {
      status?: 'ready';
      q?: string;
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<{ items: FileItem[]; total: number }> {
    const data = (await this.command('files.list', {
      flat: true,
      ...(opts.status ? { status: opts.status } : {}),
      ...(opts.q ? { q: opts.q } : {}),
      page: opts.page ?? 1,
      pageSize: opts.pageSize ?? 25,
    })) as { items?: FileItem[]; total?: number };
    return {
      items: (Array.isArray(data.items) ? data.items : []).map((item) => ({
        ...item,
        kind: 'file' as const,
      })),
      total: data.total ?? 0,
    };
  }

  async mkdir(parentId: string | null, name: string): Promise<FolderItem> {
    return requireItem<FolderItem>(
      await this.command('files.mkdir', { parentId, name })
    );
  }

  async renameFolder(id: string, name: string): Promise<FolderItem> {
    return requireItem<FolderItem>(
      await this.command('files.renameFolder', { id, name })
    );
  }

  async deleteFolder(id: string): Promise<void> {
    await this.command('files.deleteFolder', { id });
  }

  async move(
    id: string,
    folderId: string | null,
    replaceId?: string
  ): Promise<FileItem> {
    return requireItem<FileItem>(
      await this.command(
        'files.move',
        replaceId ? { id, folderId, replaceId } : { id, folderId }
      )
    );
  }

  async delete(id: string): Promise<void> {
    await this.command('files.delete', { id });
  }

  async upload(
    file: File,
    language = 'eng',
    replaceId?: string,
    folderId: string | null = null,
    prepare = false
  ): Promise<FileItem> {
    if (file.size > FILES_MAX_UPLOAD_BYTES) {
      throw new GatewayError(
        'File exceeds the 50 MB upload limit',
        GatewayErrorCode.PAYLOAD_TOO_LARGE,
        { size: file.size }
      );
    }
    const body = await this.send((csrf) => {
      const form = new FormData();
      form.append('command', 'files.upload');
      form.append('language', prepare ? 'auto' : language);
      const data: Record<string, string | boolean> = {};
      if (replaceId) data.replaceId = replaceId;
      if (folderId) data.folderId = folderId;
      if (prepare) data.prepare = true;
      if (Object.keys(data).length > 0)
        form.append('data', JSON.stringify(data));
      // Re-wrap as a Blob: a File from another realm (jsdom) is rejected by FormData.append.
      form.append('files', new Blob([file], { type: file.type }), file.name);
      return { headers: buildProxyHeaders(csrf, false), body: form };
    });
    const item = ((await body.json()) as { data?: { item?: FileItem } }).data
      ?.item;
    if (!item) {
      throw new GatewayError(
        'Invalid upload response',
        GatewayErrorCode.NETWORK_ERROR,
        { status: 200 }
      );
    }
    return item;
  }

  async download(id: string): Promise<File> {
    const response = await this.send((csrf) => ({
      headers: buildProxyHeaders(csrf),
      body: JSON.stringify({
        command: 'files.download',
        language: 'eng',
        data: JSON.stringify({ id }),
      }),
    }));
    const blob = await response.blob();
    const type = response.headers.get('Content-Type') ?? blob.type;
    return new File(
      [blob],
      filenameFrom(response.headers.get('Content-Disposition')) ?? id,
      { type }
    );
  }

  private async command(
    command: string,
    data: Record<string, unknown>
  ): Promise<unknown> {
    const response = await this.send((csrf) => ({
      headers: buildProxyHeaders(csrf),
      body: JSON.stringify({
        command,
        language: 'eng',
        data: JSON.stringify(data),
      }),
    }));
    return ((await response.json()) as { data?: unknown }).data ?? {};
  }

  /** One authorized POST to the proxy; returns the OK response, throws typed errors. */
  private send(build: ProxyRequestBuilder): Promise<Response> {
    return proxyPost(this.options, build, {
      label: 'Files',
      onFailure: async (response) => {
        if (response.status === 409 || response.status === 400) {
          await throwIfCoded(response, CODED, 'Files');
        }
      },
    });
  }
}

/** Proxy codes in a 400/409 body that imsight acts on (#93, #123). */
const CODED: Record<string, GatewayErrorCode> = {
  DUPLICATE_NAME: GatewayErrorCode.DUPLICATE_NAME,
  FOLDER_NOT_EMPTY: GatewayErrorCode.FOLDER_NOT_EMPTY,
  INVALID_INPUT: GatewayErrorCode.INVALID_INPUT,
  INVALID_ID: GatewayErrorCode.INVALID_INPUT,
};

function requireItem<T>(data: unknown): T {
  const item = (data as { item?: T } | null)?.item;
  if (!item) {
    throw new GatewayError(
      'Invalid files response',
      GatewayErrorCode.INVALID_RESPONSE,
      { status: 200 }
    );
  }
  return item;
}

/** The document a 409 DUPLICATE_NAME names as `existing`, if it does. */
export function duplicateOf(error: unknown): FileItem | null {
  if (!isGatewayError(error, GatewayErrorCode.DUPLICATE_NAME)) return null;
  return (error.details?.existing as FileItem | undefined) ?? null;
}

/** filename* (RFC 5987) wins over filename; only the base name, never a path. */
function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]?.trim();
  const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1]?.trim() ?? null;
  let name: string | null = plain;
  try {
    name = star ? decodeURIComponent(star) : plain;
  } catch {
    name = plain;
  }
  const base = name?.split(/[\\/]/).pop() ?? '';
  return base === '' || base === '.' || base === '..' ? null : base;
}
